import { getLoanStatusesByLoanId, LoanStatus, LoanStatusExternal, PaymentMethodType } from '@nemuru-eng/js-api-client';

import {
  LoanStatusProgressStages,
  LoanStatusViewStatus,
  MilestoneProgressStages,
  NonActiveProgressStages,
} from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/PaymentMethods/PaymentMethod/_shared/entities/Progress/_shared/types';
import { AbstractProgress } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/PaymentMethods/PaymentMethod/_shared/entities/Progress/AbstractProgress';
import { Milestone } from '@/components/modules/Funnel/types/Milestone';

export class LoanProgress extends AbstractProgress {
  protected _statuses: LoanStatusViewStatus[] | undefined = undefined;

  private readonly NON_ACTIVE_STATUSES = Object.keys(LOAN_PROGRESS_NON_ACTIVE_STAGES) as LoanStatus[];

  private readonly COMPLETED_STATUSES = [
    LoanStatus.STATUS_ACTIVE_TRIAL,
    LoanStatus.STATUS_ACTIVE,
    LoanStatus.STATUS_ENDED,
    LoanStatus.STATUS_UNPAID,
  ];

  constructor(loanId: string, paymentMethodType: PaymentMethodType) {
    super(loanId, paymentMethodType, LOAN_PROGRESS_STAGES);
  }

  protected async fetchStatuses(): Promise<LoanStatusViewStatus[]> {
    const loanStatuses = await getLoanStatusesByLoanId({ loanId: this._paymentMethodId });
    return loanStatuses.statuses;
  }

  protected getStatusName(status: LoanStatus | LoanStatusExternal) {
    return status;
  }

  protected getNonActiveStages(): NonActiveProgressStages {
    return LOAN_PROGRESS_NON_ACTIVE_STAGES;
  }

  protected isNonActiveStatus(status: LoanStatus): boolean {
    return this.NON_ACTIVE_STATUSES.includes(status);
  }

  protected isCompletedStatus(status: LoanStatus): boolean {
    return this.COMPLETED_STATUSES.includes(status);
  }

  public get statuses(): LoanStatusViewStatus[] | undefined {
    return this._statuses;
  }
}

const LOAN_PROGRESS_STAGES: MilestoneProgressStages = {
  [Milestone.INITIAL]: [
    {
      nameTextId: Milestone.INITIAL,
      status: LoanStatus.STATUS_INITIAL,
      isMainStep: true,
      checkpointGroup: 1,
    },
    //{
    //  nameTextId: 'SMS_OPEN', // TODO-ELSA? –– 'OPENED'
    //  status: LoanStatus.STATUS_OPENED,
    //  isMainStep: false,
    //  checkpointGroup: 1,
    //},
    {
      nameTextId: 'PERSONAL_INFORMATION',
      status: LoanStatus.STATUS_KYC_SET,
      isMainStep: false,
      checkpointGroup: 1,
    },
    {
      nameTextId: 'FINANCIAL_INFORMATION',
      status: LoanStatus.STATUS_INCOME_SET,
      isMainStep: false,
      checkpointGroup: 1,
    },
    {
      nameTextId: 'PROFESSIONAL_INFORMATION',
      status: LoanStatus.STATUS_PROFESSIONAL_DETAILS_SET,
      isMainStep: false,
      checkpointGroup: 1,
    },
    //{
    //  nameTextId: 'RISK_INFORMATION',
    //  status: LoanStatus.STATUS_RISK_SET,
    //  isMainStep: false,
    //  checkpointGroup: 1,
    //},
  ],
  [Milestone.REQUESTED]: [
    {
      nameTextId: Milestone.REQUESTED,
      status: LoanStatus.STATUS_ANALYSING,
      isMainStep: true,
      checkpointGroup: 2,
    },
  ],
  [Milestone.APPROVED]: [
    {
      nameTextId: Milestone.APPROVED,
      status: LoanStatus.STATUS_LENDER_HAS_BEEN_SELECTED,
      isMainStep: true,
      checkpointGroup: 2,
    },
    {
      nameTextId: 'CONTRACT_SENT',
      status: LoanStatus.STATUS_CONTRACT_SENT,
      isMainStep: false,
      checkpointGroup: 2,
    },
    {
      nameTextId: 'SIGNED_NOT_FORMALIZED', // TODO-ELSA? –– 'VALIDATING_DOCUMENTATION'
      status: LoanStatus.STATUS_CONTRACT_SIGNED,
      isMainStep: false,
      checkpointGroup: 2,
    },
  ],
  [Milestone.CONFIRMED]: [
    {
      nameTextId: Milestone.CONFIRMED,
      status: LoanStatus.STATUS_DOCS_APPROVED,
      isMainStep: true,
      checkpointGroup: 2,
    },
  ],
  [Milestone.PAID]: [
    {
      nameTextId: Milestone.PAID,
      status: LoanStatus.STATUS_ACTIVE_TRIAL,
      isMainStep: true,
      checkpointGroup: 3,
    },
    {
      nameTextId: Milestone.PAID,
      status: LoanStatus.STATUS_ACTIVE,
      isMainStep: false,
      checkpointGroup: 3,
      isHidden: true,
    },
  ],
};

const LOAN_PROGRESS_NON_ACTIVE_STAGES: LoanStatusProgressStages = {
  [LoanStatus.STATUS_DENIED]: [
    {
      nameTextId: 'DENIED',
      status: LoanStatus.STATUS_DENIED,
      isMainStep: true,
      checkpointGroup: 0,
    },
  ],
  [LoanStatus.STATUS_EXPIRED]: [
    {
      nameTextId: 'EXPIRED',
      status: LoanStatus.STATUS_EXPIRED,
      isMainStep: true,
      checkpointGroup: 0,
    },
  ],
  [LoanStatus.STATUS_CANCELLED]: [
    {
      nameTextId: 'CANCELLED',
      status: LoanStatus.STATUS_CANCELLED,
      isMainStep: true,
      checkpointGroup: 0,
    },
  ],
  [LoanStatus.STATUS_CANCELLED_TEST]: [
    {
      nameTextId: 'CANCELLED_TEST',
      status: LoanStatus.STATUS_CANCELLED_TEST,
      isMainStep: true,
      checkpointGroup: 0,
    },
  ],
  [LoanStatus.STATUS_DESISTED]: [
    {
      nameTextId: 'DESISTED',
      status: LoanStatus.STATUS_DESISTED,
      isMainStep: true,
      checkpointGroup: 0,
    },
  ],
};
